import { Link } from "react-router-dom";
import RevealOnScroll from "../../components/RevealOnScroll";
import { useBrand } from "../../context/BrandContext";
import useParallaxScroll from "../../hooks/useParallaxScroll";
import MarketplaceShell from "./MarketplaceShell";
import "./MarketplaceLanding.css";

const focusRingClass =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500";

const primaryButtonClass = `inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-rose-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-600 sm:w-auto ${focusRingClass}`;
const secondaryButtonClass = `inline-flex min-h-11 w-full items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 transition hover:border-slate-400 hover:bg-slate-50 sm:w-auto ${focusRingClass}`;
const cardClass =
  "min-w-0 rounded-[28px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(47,53,102,0.12)]";
const smallCardClass =
  "rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_14px_45px_rgba(47,53,102,0.08)]";

const heroPreviewCards = [
  {
    title: "Available Workspace",
    value: "Chair, room, or station",
    body: "Spaces are easier to compare when the details are clear.",
  },
  {
    title: "Rental Request",
    value: "Review before accepting",
    body: "Salon owners can look over requests before moving forward.",
  },
  {
    title: "Rental Details",
    value: "Pricing, amenities, expectations",
    body: "Independent professionals can review what matters before they book.",
  },
];

const audienceCards = [
  {
    title: "For Salon Owners",
    body:
      "List available chairs, rooms, or stations, review rental requests, and manage workspace activity in one place.",
    cta: "List Your Workspace",
    to: "/account/operator/workspaces/new",
    image: "/marketplace-placeholders/loc02.jpeg",
    imageAlt: "Bright salon workspace with pedicure chairs and a reception desk.",
  },
  {
    title: "For Independent Professionals",
    body:
      "Find a workspace that fits your work, review rental details, and manage your rentals as your business grows.",
    cta: "Explore Workspaces",
    to: "/search",
    image: "/salon-desk2-2.jpg",
    imageAlt: "Clean manicure stations inside a modern salon workspace.",
  },
];

const mediaStripCards = [
  {
    title: "Search workspaces",
    body: "Browse available chairs, rooms, or stations.",
    image: "/marketplace-placeholders/loc01.jpeg",
    alt: "Bright salon interior with open chairs and work areas.",
  },
  {
    title: "Review rental details",
    body: "See pricing, amenities, photos, and expectations.",
    image: "/marketplace-placeholders/loc03.jpeg",
    alt: "Modern salon station prepared for professional workspace rental.",
  },
  {
    title: "Manage requests",
    body: "Keep rental requests and agreements organized.",
    image: "/marketplace-placeholders/loc02.jpeg",
    alt: "Clean salon workspace with multiple stations ready for use.",
  },
];

const pathCards = [
  {
    label: "Salon Owners",
    title: "Turn available salon space into organized rental activity.",
    body:
      "Create clear workspace listings, keep rental requests organized, and manage the details your salon needs before someone starts working in your space.",
    bullets: [
      "List chairs, rooms, or stations",
      "Review workspace rental requests",
      "Manage rental agreements",
    ],
    cta: "List Your Workspace",
    to: "/account/operator/workspaces/new",
    image: "/marketplace-placeholders/loc02.jpeg",
    imageAlt: "Salon chairs and stations arranged for workspace rental.",
  },
  {
    label: "Independent Professionals",
    title: "Find a professional place to serve your clients.",
    body:
      "Browse workspaces, compare important details, and choose a setting where you can work comfortably and build your client business.",
    bullets: [
      "Search available workspaces",
      "Review rental details before you request",
      "Keep rentals and client work organized",
    ],
    cta: "Explore Workspaces",
    to: "/search",
    image: "/salon-desk2-2.jpg",
    imageAlt: "Professional manicure workspace with clean salon stations.",
  },
];

const workflowSteps = [
  {
    title: "List or find workspace",
    body:
      "Salon owners can list available workspace. Independent professionals can search for a place that fits their work.",
  },
  {
    title: "Review the details",
    body:
      "Workspace photos, pricing, amenities, availability, and expectations are easier to compare before moving forward.",
  },
  {
    title: "Manage rental activity",
    body:
      "Rental requests, agreement terms, and workspace records stay organized in MyBoothShare.",
  },
];

const benefitCards = [
  {
    title: "Clear expectations",
    body:
      "Workspace details, rental terms, and next steps are easier to review before anyone commits.",
  },
  {
    title: "Organized follow-up",
    body:
      "Bookings, requests, and workspace activity stay easier to track for both sides.",
  },
  {
    title: "Connected business tools",
    body:
      "Open Ellyanna Booking when you need to manage appointments, clients, services, sales, and daily operations.",
  },
];

function Badge({ children }) {
  return (
    <p className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
      {children}
    </p>
  );
}

function Icon({ name }) {
  const shared = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true">
      {name === "search" && (
        <>
          <circle cx="10.5" cy="10.5" r="6.5" {...shared} />
          <path d="m15.5 15.5 4.5 4.5" {...shared} />
        </>
      )}
      {name === "details" && (
        <>
          <rect x="4" y="4" width="16" height="16" rx="3" {...shared} />
          <path d="M8 9h8M8 13h8M8 17h5" {...shared} />
        </>
      )}
      {name === "request" && (
        <>
          <path d="M5 7.5h14v11H5z" {...shared} />
          <path d="m5 8 7 5 7-5" {...shared} />
          <path d="M8 4.5h8" {...shared} />
        </>
      )}
    </svg>
  );
}

function HeroPreviewCard({ title, value, body, delay }) {
  return (
    <div
      className="myboothshare-float-soft rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_14px_38px_rgba(47,53,102,0.08)]"
      style={{ animationDelay: `${delay}ms` }}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
        {title}
      </p>
      <p className="mt-1 text-sm font-semibold text-slate-950">{value}</p>
      <p className="mt-1 text-xs leading-5 text-slate-500">{body}</p>
    </div>
  );
}

function HeroVisual({ parallaxStyle }) {
  return (
    <div className={`${cardClass} relative overflow-hidden p-5 md:p-6`}>
      <div
        className="absolute -right-12 -top-12 h-36 w-36 rounded-full bg-rose-200/45 blur-2xl"
        aria-hidden="true"
        style={parallaxStyle}
      />
      <div className="relative overflow-hidden rounded-[24px] border border-slate-200 bg-slate-950">
        <div className="aspect-[16/11] min-h-[300px]">
          <img
            src="/marketplace-placeholders/loc01.jpeg"
            alt="Bright salon workspace with chairs and stations available for professional use."
            className="h-full w-full object-cover opacity-80"
            loading="eager"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/30 via-slate-950/5 to-rose-500/20" />
        <div className="absolute bottom-4 left-4 right-4 rounded-2xl border border-white/25 bg-white/90 p-4 shadow-2xl backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-rose-500">
                Workspace Activity
              </p>
              <p className="mt-1 text-sm font-semibold text-slate-950">
                Bookings and rental records in one place
              </p>
            </div>
            <span className="rounded-full bg-slate-950 px-3 py-1 text-xs font-semibold text-white">
              Ready for review
            </span>
          </div>
        </div>
      </div>
      <div className="relative mt-4 grid gap-3 md:grid-cols-3">
        {heroPreviewCards.map((card, index) => (
          <HeroPreviewCard key={card.title} {...card} delay={index * 350} />
        ))}
      </div>
    </div>
  );
}

function AudienceCard({ title, body, cta, to, image, imageAlt, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_45px_rgba(47,53,102,0.08)]"
    >
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        <img src={image} alt={imageAlt} className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="p-5">
        <h2 className="text-lg font-semibold text-slate-950">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
        <Link to={to} className="mt-4 inline-flex text-sm font-semibold text-rose-500 hover:text-rose-600">
          {cta}
        </Link>
      </div>
    </RevealOnScroll>
  );
}

function MediaStripCard({ title, body, image, alt, delay }) {
  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_45px_rgba(47,53,102,0.08)]"
    >
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        <img src={image} alt={alt} className="h-full w-full object-cover" loading="lazy" />
      </div>
      <div className="p-5">
        <h3 className="text-lg font-semibold text-slate-950">{title}</h3>
        <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
      </div>
    </RevealOnScroll>
  );
}

function PathCard({ label, title, body, bullets, cta, to, image, imageAlt, delay }) {
  const isOwner = label === "Salon Owners";

  return (
    <RevealOnScroll
      as="article"
      delay={delay}
      className={`${smallCardClass} grid h-full min-w-0 gap-5 md:grid-cols-[0.88fr,1.12fr] md:items-center`}
    >
      <div className="overflow-hidden rounded-2xl bg-slate-100">
        <img
          src={image}
          alt={imageAlt}
          className="aspect-[4/3] h-full w-full object-cover"
          loading="lazy"
        />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-rose-500">
          {label}
        </p>
        <h3 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
          {title}
        </h3>
        <p className="mt-3 text-sm leading-6 text-slate-600">{body}</p>
        <ul className="mt-5 space-y-3 text-sm font-medium text-slate-700">
          {bullets.map((bullet) => (
            <li key={bullet} className="flex gap-3">
              <span
                className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-rose-400"
                aria-hidden="true"
              />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6">
          <Link to={to} className={isOwner ? secondaryButtonClass : primaryButtonClass}>
            {cta}
          </Link>
        </div>
      </div>
    </RevealOnScroll>
  );
}

function WorkflowCard({ title, body, index }) {
  const iconNames = ["search", "details", "request"];

  return (
    <RevealOnScroll
      as="article"
      delay={index * 120}
      className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
        <Icon name={iconNames[index]} />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-slate-950">{title}</h3>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
    </RevealOnScroll>
  );
}

function ProductContrastCard({ title, label, items, tone }) {
  const isRose = tone === "rose";

  return (
    <div className={`rounded-2xl border p-5 ${isRose ? "border-rose-200 bg-rose-50/70" : "border-slate-200 bg-slate-50"}`}>
      <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isRose ? "text-rose-500" : "text-slate-500"}`}>
        {label}
      </p>
      <h3 className="mt-2 text-xl font-semibold text-slate-950">{title}</h3>
      <ul className="mt-4 space-y-2 text-sm leading-6 text-slate-600">
        {items.map((item) => (
          <li key={item} className="flex gap-2">
            <span className={`mt-2 h-2 w-2 shrink-0 rounded-full ${isRose ? "bg-rose-400" : "bg-slate-400"}`} aria-hidden="true" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MarketplaceLanding() {
  const runtimeBrand = useBrand();
  const parallax = useParallaxScroll();
  const opsHref = runtimeBrand.opsHomeUrl || "https://ellyanna.com/";

  return (
    <MarketplaceShell>
      <div className="myboothshare-home-surface -mx-4 -my-8 bg-[radial-gradient(circle_at_top_left,rgba(255,236,239,0.92),transparent_34%),linear-gradient(135deg,#fff7f8_0%,#f4f7ff_100%)] px-4 py-8 md:-mx-6 md:px-6">
        <div className="myboothshare-parallax-layer" aria-hidden="true">
          <div className="myboothshare-blob myboothshare-blob-rose" style={parallax.hero} />
          <div className="myboothshare-blob myboothshare-blob-indigo" style={parallax.mid} />
        </div>
        <div className="relative mx-auto max-w-6xl space-y-7 pb-2">
          <section
            className="grid gap-5 lg:grid-cols-[1.02fr,0.98fr] lg:items-stretch"
            aria-labelledby="myboothshare-hero-title"
          >
            <RevealOnScroll className={`${cardClass} p-6 md:p-8`}>
              <Badge>Ellyanna MyBoothShare</Badge>
              <h1
                id="myboothshare-hero-title"
                className="mt-6 max-w-3xl break-words text-4xl font-semibold tracking-tight text-slate-950 md:text-5xl md:leading-tight"
              >
                A professional workspace hub for salons and independent beauty
                professionals.
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 md:text-lg">
                MyBoothShare helps salon owners manage available workspace and
                helps independent professionals find a place to serve clients
                with confidence.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Link to="/search" className={primaryButtonClass}>
                  Explore Workspaces
                </Link>
                <Link to="/account/operator/workspaces/new" className={secondaryButtonClass}>
                  List Your Workspace
                </Link>
              </div>
              <p className="mt-4 text-sm leading-6 text-slate-500">
                Sign in when you are ready to manage rental requests, listings, or
                rental activity.
              </p>
            </RevealOnScroll>

            <RevealOnScroll delay={120} className="min-w-0">
              <HeroVisual parallaxStyle={parallax.mid} />
            </RevealOnScroll>
          </section>

          <section className="grid gap-4 md:grid-cols-3" aria-label="MyBoothShare workflow preview cards">
            {mediaStripCards.map((card, index) => (
              <MediaStripCard key={card.title} {...card} delay={index * 120} />
            ))}
          </section>

          <section className="px-1 py-3 md:px-2" aria-labelledby="path-heading">
            <RevealOnScroll className="max-w-3xl">
              <h2
                id="path-heading"
                className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
              >
                Choose the path that fits you today.
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                Start by browsing workspaces or listing available space. You can
                sign in later when you need to manage rental requests, listings, or
                workspace settings.
              </p>
            </RevealOnScroll>
            <div className="mt-6 grid gap-5">
              {pathCards.map((card, index) => (
                <PathCard key={card.label} {...card} delay={index * 120} />
              ))}
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-2" aria-label="MyBoothShare audience cards">
            {audienceCards.map((card, index) => (
              <AudienceCard key={card.title} {...card} delay={index * 120} />
            ))}
          </section>

          <section className={`${cardClass} px-6 py-7 md:px-8`} aria-labelledby="how-myboothshare-helps">
            <RevealOnScroll className="max-w-3xl">
              <h2
                id="how-myboothshare-helps"
                className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
              >
                How MyBoothShare helps both sides stay organized
              </h2>
              <p className="mt-3 text-base leading-7 text-slate-600">
                A clear workspace rental process helps salon owners and
                independent professionals move forward with less confusion.
              </p>
            </RevealOnScroll>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {workflowSteps.map((step, index) => (
                <WorkflowCard key={step.title} {...step} index={index} />
              ))}
            </div>
          </section>

          <section className="px-1 py-3 md:px-2" aria-labelledby="relationships-heading">
            <RevealOnScroll>
              <h2
                id="relationships-heading"
                className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
              >
                Built for clear workspace relationships.
              </h2>
            </RevealOnScroll>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {benefitCards.map((card, index) => (
                <RevealOnScroll
                  as="article"
                  key={card.title}
                  delay={index * 100}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-5"
                >
                  <h3 className="text-lg font-semibold text-slate-950">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">
                    {card.body}
                  </p>
                </RevealOnScroll>
              ))}
            </div>
          </section>

          <section className={`${cardClass} px-6 py-7 md:px-8`} aria-labelledby="ellyanna-booking-cross-link">
            <div className="grid gap-6 lg:grid-cols-[1fr,1.1fr] lg:items-center">
              <RevealOnScroll>
                <h2
                  id="ellyanna-booking-cross-link"
                  className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
                >
                  Need business management tools too?
                </h2>
                <p className="mt-3 max-w-3xl text-base leading-7 text-slate-600">
                  Ellyanna Booking is where salon owners and independent
                  professionals manage appointments, clients, services, sales,
                  and daily operations.
                </p>
                <a href={opsHref} className={`${secondaryButtonClass} mt-6`}>
                  Open Ellyanna Booking
                </a>
              </RevealOnScroll>
              <RevealOnScroll delay={120} className="grid gap-4 md:grid-cols-2">
                <ProductContrastCard
                  label="MyBoothShare"
                  title="Workspace rental activity"
                  tone="rose"
                  items={["Workspace listings", "Rental requests", "Chairs, rooms, or stations"]}
                />
                <ProductContrastCard
                  label="Ellyanna Booking"
                  title="Client booking and salon management"
                  items={["Appointments", "Clients and services", "Sales and reports"]}
                />
              </RevealOnScroll>
            </div>
          </section>

          <section
            className="relative overflow-hidden rounded-[28px] border border-white/80 bg-gradient-to-br from-white via-rose-50 to-indigo-50 px-6 py-8 shadow-[0_24px_80px_rgba(47,53,102,0.12)] md:px-8"
            aria-labelledby="final-cta-title"
          >
            <RevealOnScroll className="grid gap-5 lg:grid-cols-[1fr,auto] lg:items-center">
              <div>
                <h2
                  id="final-cta-title"
                  className="break-words text-3xl font-semibold tracking-tight text-slate-950 md:text-4xl"
                >
                  Start with the path that fits you today.
                </h2>
                <p className="mt-3 text-base leading-7 text-slate-600">
                  Explore available workspaces, list a workspace for your salon, or
                  sign in when you are ready to manage your rental activity.
                </p>
              </div>
              <div className="flex flex-col items-start gap-3 lg:items-end">
                <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-semibold text-slate-700 shadow-lg myboothshare-float-soft-slow" aria-hidden="true">
                  Spaces, requests, records
                </div>
                <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap lg:justify-end">
                  <Link to="/search" className={primaryButtonClass}>
                    Explore Workspaces
                  </Link>
                  <Link to="/account/operator/workspaces/new" className={secondaryButtonClass}>
                    List Your Workspace
                  </Link>
                  <Link to="/login" className={secondaryButtonClass}>
                    Sign in
                  </Link>
                </div>
              </div>
            </RevealOnScroll>
          </section>
        </div>
      </div>
    </MarketplaceShell>
  );
}
